import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { UnknownAction } from '@reduxjs/toolkit'

import { SettingName } from '../../../userapp/components/user-profile-language-select'
import { CurrentUser } from '../../../lib/currentUser'
import { getPortalState } from '../../../lib/portalDomain'
import { readCsrfTokenFromResponse, setCsrfToken } from '../../utils/auth/csrf'
import getChargeAmpsApiPath from '../../../lib/getChargeAmpsApiPath'
import http from '../../../lib/http'
import storage from '../../utils/storage'
import {
    AuthenticationServiceContextRequest,
    UserChargePointExistenceResponse,
    enhancedApi as webApi,
    useLazyAuthGetCurrentQuery,
    useAuthLoginMutation,
    useEntitlementsCreateContextTokenMutation,
    useLazyCompanyGetAdministratedCompaniesQuery,
    useLazyUserGetUserSettingsQuery,
    useLazyUserIsUserHasChargePointsQuery,
    useUserUpdateUserSettingsMutation,
} from '../../redux/api/web-api'
import { clearEntitlements } from '../../redux/features/entitlements/entitlementsSlice'
import { notifyError } from '../../features/notificationsManager/Notify'
import { triggerAuthChange } from '../../utils/broadcastChannel'
import { isFetchBaseQueryError } from '../../redux/api/fetchBaseQueryWithReauth'
import { useToast } from '../../uikit/ToastProvider'
import { useApplyOrganisationSelection } from '../../hooks/useApplyOrganisationSelection'
import { buildPortalSessionSnapshot, canHydratePortalSession } from '../../hooks/portalSessionState'
import { useRejectedPortalLoginCleanup } from '../../hooks/useRejectedPortalLoginCleanup'
import useText from '../../hooks/useText'

type LoginCredentials = {
    email: string
    password: string
}

const PORTAL_ACCESS_DENIED_MESSAGE = 'Portal access denied'

type UsePortalLoginFlowArgs = {
    from: string
    locale: string
    setLocale: (locale: string) => void
    setNotification: (notification: { text: string; type?: string } | null) => void
    navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => void
}

// Own the portal login side effects so the form component only handles validation and rendering.
export const usePortalLoginFlow = ({ from, locale, setLocale, setNotification, navigate }: UsePortalLoginFlowArgs) => {
    const text = useText()
    const toast = useToast()
    const dispatch = useDispatch()
    const portal = getPortalState(window.location.pathname)
    const applyOrganisationSelection = useApplyOrganisationSelection()
    const cleanupRejectedPortalLogin = useRejectedPortalLoginCleanup()
    const dispatchApiAction = useCallback((action: unknown) => dispatch(action as UnknownAction), [dispatch])

    const [login, { isLoading: loginIsLoading }] = useAuthLoginMutation()
    const [getAuthCurrent, { isFetching: authCurrentIsLoading }] = useLazyAuthGetCurrentQuery()
    const [getUserHasChargePoints, { isLoading: chargePointIsLoading }] = useLazyUserIsUserHasChargePointsQuery()
    const [getAdminCompanies] = useLazyCompanyGetAdministratedCompaniesQuery()
    const [getUserSettings] = useLazyUserGetUserSettingsQuery()
    const [saveUserSettings] = useUserUpdateUserSettingsMutation()
    const [getEntitlements] = useEntitlementsCreateContextTokenMutation()

    const getPortalAccessDeniedMessage = useCallback((): string => {
        if (portal.isAdminDomain) {
            return text('msgDeniedForNonAdministrators')
        }

        if (portal.isPartnerDomain) {
            return text('msgDeniedForNonPartners')
        }

        if (portal.isSupportDomain) {
            return text('msgAccessDeniedMustBeAdmin')
        }

        return text('lblSomethingWentWrong')
    }, [portal.isAdminDomain, portal.isPartnerDomain, portal.isSupportDomain, text])

    const getLoginErrorMessage = useCallback(
        (error: unknown): string => {
            if (isFetchBaseQueryError(error)) {
                if (error.status === 401) {
                    return text('msgWrongCredentialsError')
                }

                const errorMessage =
                    typeof error.data === 'object' && error.data !== null && 'message' in error.data
                        ? error.data.message
                        : null

                if (error.status === 403 && errorMessage === PORTAL_ACCESS_DENIED_MESSAGE) {
                    return getPortalAccessDeniedMessage()
                }
            }

            return text('lblSomethingWentWrong')
        },
        [getPortalAccessDeniedMessage, text]
    )

    const checkChargePoints = useCallback(async (): Promise<boolean> => {
        try {
            const chargePointsExistence: UserChargePointExistenceResponse = await getUserHasChargePoints().unwrap()
            const hasChargePoints = chargePointsExistence.hasChargePoints === true

            if (hasChargePoints) {
                navigate(from, { replace: true })
            } else {
                navigate('/registerchargepoint', { replace: true })
            }

            return true
        } catch {
            setNotification({ text: 'msgPleaseTryLoginLater', type: 'alert' })
            await cleanupRejectedPortalLogin()
            return false
        }
    }, [cleanupRejectedPortalLogin, from, getUserHasChargePoints, navigate, setNotification])

    const checkForAvailableOrganisations = useCallback(
        async (user: CurrentUser): Promise<boolean> => {
            try {
                const { data: organisations, isError, error } = await getAdminCompanies(undefined)

                if (isError) throw error

                if (user.selectedCompanyId) {
                    try {
                        const authenticationServiceContextRequest: AuthenticationServiceContextRequest = {
                            organizationId: user.selectedCompanyId,
                        }

                        await getEntitlements({ authenticationServiceContextRequest }).unwrap()

                        await applyOrganisationSelection({
                            organizationId: user.selectedCompanyId,
                            broadcastAuthChange: false,
                        })
                    } catch {
                        dispatch(clearEntitlements())
                        toast.notifyError(text('lblCouldNotGetEntitlements'))
                        await cleanupRejectedPortalLogin()
                        return false
                    }

                    navigate(from, { replace: true })
                    return true
                }

                if (organisations?.length === 1) {
                    const organizationId = organisations[0]?.id

                    if (!organizationId) {
                        throw new Error('Single organisation is missing id')
                    }

                    try {
                        const authenticationServiceContextRequest: AuthenticationServiceContextRequest = {
                            organizationId,
                        }

                        await getEntitlements({ authenticationServiceContextRequest }).unwrap()

                        await applyOrganisationSelection({
                            organizationId,
                            broadcastAuthChange: false,
                        })
                    } catch {
                        dispatch(clearEntitlements())
                        toast.notifyError(text('lblCouldNotGetEntitlements'))
                        await cleanupRejectedPortalLogin()
                        return false
                    }

                    navigate(from, { replace: true })
                    return true
                }

                navigate('/select-organisation', { replace: true, state: { from } })
                return true
            } catch (exception: unknown) {
                notifyError(exception instanceof Error ? exception.message : String(exception))
                await cleanupRejectedPortalLogin()
                return false
            }
        },
        [
            applyOrganisationSelection,
            cleanupRejectedPortalLogin,
            dispatch,
            from,
            getAdminCompanies,
            getEntitlements,
            navigate,
            text,
            toast,
        ]
    )

    const loadAuthenticatedPortalSession = useCallback(async (): Promise<CurrentUser> => {
        dispatchApiAction(webApi.util.resetApiState())

        const sessionData = await getAuthCurrent(undefined, false).unwrap()
        const sessionSnapshot = buildPortalSessionSnapshot(sessionData, {
            currentDomain: portal.currentDomain,
            currentPortalAudience: portal.currentPortalAudience,
        })

        if (!sessionSnapshot.sessionUser || !canHydratePortalSession(sessionSnapshot, false)) {
            throw new Error('Authenticated portal session could not be hydrated')
        }

        return sessionSnapshot.sessionUser
    }, [dispatchApiAction, getAuthCurrent, portal.currentDomain, portal.currentPortalAudience])

    const handleAuthenticatedLogin = useCallback(async (): Promise<boolean> => {
        let normalizedUser: CurrentUser

        try {
            normalizedUser = await loadAuthenticatedPortalSession()
        } catch {
            setNotification({ text: 'msgPleaseTryLoginLater', type: 'alert' })
            await cleanupRejectedPortalLogin()
            return false
        }

        if (portal.isPortalDomain) {
            try {
                const response = await http<Response>({
                    url: `${getChargeAmpsApiPath()}/csrf/handshake`,
                    method: 'GET',
                    credentials: 'include',
                })
                if (response.status < 400) {
                    setCsrfToken(readCsrfTokenFromResponse(response))
                }
            } catch {
                // Authenticated unsafe requests will bootstrap CSRF on demand if needed.
            }
        }

        try {
            const { data: settingsData } = await getUserSettings({ userId: normalizedUser.id })
            const settings = Array.isArray(settingsData) ? settingsData : []

            if (settings.length > 0) {
                const savedLanguage = settings.find((setting) => setting.name === SettingName.LANGUAGE)?.value

                if (!savedLanguage || locale !== savedLanguage) {
                    await saveUserSettings({
                        userId: normalizedUser.id,
                        body: [
                            {
                                name: SettingName.LANGUAGE,
                                value: locale,
                            },
                        ],
                    }).unwrap()
                } else if (savedLanguage) {
                    setLocale(savedLanguage)
                }
            }

            const hasGreenChargingInformed = settings.some(
                (setting) => setting.name === SettingName.GREEN_CHARGING_INFORMED
            )

            if (portal.isUserappDomain && !hasGreenChargingInformed) {
                storage.setItem('showGreenChargingModal', 'true')
            }
        } catch (error) {
            notifyError(`Cannot load user settings: ${error}`)
        }

        if (portal.isUserappDomain) {
            if (!normalizedUser.personalAccept && !normalizedUser.companyAccept) {
                navigate('/user-agreement', { replace: true })
                return true
            }

            return checkChargePoints()
        }

        if (portal.isAdminDomain) {
            return checkForAvailableOrganisations(normalizedUser)
        }

        navigate(from, { replace: true })
        return true
    }, [
        checkChargePoints,
        checkForAvailableOrganisations,
        from,
        getUserSettings,
        loadAuthenticatedPortalSession,
        locale,
        navigate,
        portal.isAdminDomain,
        portal.isPortalDomain,
        portal.isUserappDomain,
        saveUserSettings,
        setNotification,
        setLocale,
        cleanupRejectedPortalLogin,
    ])

    const submitLogin = useCallback(
        async (credentials: LoginCredentials): Promise<string | null> => {
            try {
                await login({ userCredentials: credentials }).unwrap()
                const loginSucceeded = await handleAuthenticatedLogin()
                if (loginSucceeded) {
                    triggerAuthChange()
                }
                return null
            } catch (error) {
                return getLoginErrorMessage(error)
            }
        },
        [getLoginErrorMessage, handleAuthenticatedLogin, login]
    )

    return {
        loading: loginIsLoading || authCurrentIsLoading || chargePointIsLoading,
        submitLogin,
    }
}
